import * as React from 'react';
import { useXyqarAuth } from '@xyqar/shared-auth';
import { apiGet } from '../lib/api';
import {
  PageTitle, Card, Pill, Section, Kpi, Table, Th, Td, Btn, fmt,
} from '../components/ui-kit';
import { Anchor, Bitcoin, Clock } from 'lucide-react';

interface MerkleRoot {
  day: string;
  root: string;
  leaves: number;
  ots_anchor?: string | null;
  btc_op_return?: string | null;
  ed25519_sig?: string;
  issued_at?: string;
}

export default function MerklePage() {
  const { getToken } = useXyqarAuth();
  const [items, setItems] = React.useState<MerkleRoot[] | null>(null);
  const [err, setErr] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState<string | null>(null);

  React.useEffect(() => {
    (async () => {
      try {
        const t = await getToken().catch(() => '');
        const j = await apiGet<{ roots: MerkleRoot[] }>('/api/v1/auditor/merkle', t);
        setItems(j.roots ?? []);
      } catch (e: any) { setErr(e.message); setItems([]); }
    })();
  }, [getToken]);

  const copy = async (s: string) => { try { await navigator.clipboard.writeText(s); setCopied(s); setTimeout(() => setCopied(null), 1500); } catch {} };

  const totalLeaves = (items ?? []).reduce((s, r) => s + (r.leaves || 0), 0);
  const anchored = (items ?? []).filter((r) => r.ots_anchor || r.btc_op_return).length;

  return (
    <div>
      <PageTitle
        kicker="Audit · Merkle"
        title="Daily Merkle roots"
        right={<Pill tone="live"><Anchor className="h-3 w-3" /> hash-chained</Pill>}
      />

      {err && <Card className="mb-6 border-amber-500/30 bg-amber-500/5"><p className="text-amber-300 text-xs font-mono">{err}</p></Card>}

      <Section title="At a glance" hint="One root per UTC day · all events on that day are included in the tree.">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Kpi accent="blue"  label="Roots emitted"   value={fmt(items?.length)} />
          <Kpi accent="green" label="Total leaves"    value={fmt(totalLeaves)} />
          <Kpi              label="Anchored"        value={fmt(anchored)} hint="OpenTimestamps / BTC OP_RETURN" />
          <Kpi              label="Algorithm"       value="SHA-256" hint="Ed25519 signature optional" />
        </div>
      </Section>

      <Section title="Daily roots" hint="Verify any event by recomputing the Merkle proof against the day root.">
        {items === null && <Card>Loading…</Card>}
        {items && items.length > 0 && (
          <Table>
            <thead>
              <tr><Th>Day</Th><Th>Leaves</Th><Th>Root (sha-256)</Th><Th>Anchors</Th><Th></Th></tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.day} className="border-t border-white/5 hover:bg-white/5">
                  <Td>
                    <span className="text-white text-sm font-mono">{r.day}</span>
                    <p className="text-[10px] text-white/30 font-mono mt-0.5"><Clock className="inline h-2.5 w-2.5 mr-1" />{r.issued_at?.slice(0, 19).replace('T', ' ')}</p>
                  </Td>
                  <Td><span className="text-white font-mono text-sm">{fmt(r.leaves)}</span></Td>
                  <Td><span className="text-white/60 font-mono text-[10px] break-all">{r.root}</span></Td>
                  <Td>
                    <div className="flex flex-wrap gap-1">
                      {r.ots_anchor    && <Pill tone="live"><Anchor className="h-2.5 w-2.5" /> OTS</Pill>}
                      {r.btc_op_return && <Pill tone="live"><Bitcoin className="h-2.5 w-2.5" /> BTC</Pill>}
                      {!r.ots_anchor && !r.btc_op_return && <span className="text-white/30 text-xs font-mono">pending</span>}
                    </div>
                  </Td>
                  <Td className="text-right"><Btn onClick={() => copy(r.root)}>{copied === r.root ? 'Copied ✓' : 'Copy root'}</Btn></Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Section>

      <Section title="How to verify" hint="Reproducible from the audit chain export">
        <Card>
          <ol className="list-decimal list-inside text-sm text-white/70 space-y-2">
            <li>Pull the day's events: <span className="font-mono text-white">GET /api/v1/auditor/merkle/leaves?day=YYYY-MM-DD</span></li>
            <li>Recompute the Merkle root locally (SHA-256, sorted pairs).</li>
            <li>Compare to the root above — any mismatch proves tampering.</li>
            <li>Optionally check the OTS / BTC anchor to bind the root to wall-clock time.</li>
          </ol>
        </Card>
      </Section>
    </div>
  );
}
